import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Paper,
  Grid,
  Button
} from '@mui/material';
import ColorPalette from '../../../colorpalette/ColorPalette';

const MessageComposer = ({
  messageTemplate,
  messageVariables,
  onMessageTemplateChange,
  onMessageVariablesChange,
  sampleRecipient
}) => {
  const [previewMessage, setPreviewMessage] = useState('');

  // Update preview message when template or variables change
  useEffect(() => {
    if (messageTemplate) {
      let preview = messageTemplate;
      
      // Replace variables with sample values
      const replacements = {
        '{name}': sampleRecipient ? sampleRecipient.name.split(' ')[0] : 'João',
        '{fullName}': sampleRecipient ? sampleRecipient.name : 'João Silva',
        '{greetings}': getTimeBasedGreeting(),
        '{doctor_name}': messageVariables.doctor_name || 'Dr. Silva',
        '{company_name}': messageVariables.company_name || 'Clínica Exemplo',
      };
      
      // Add appointment-specific variables if available
      if (sampleRecipient && sampleRecipient.appointment_date) {
        replacements['{appointment_date}'] = formatDate(sampleRecipient.appointment_date);
      } else {
        replacements['{appointment_date}'] = '01/05/2025';
      }
      
      if (sampleRecipient && sampleRecipient.appointment_time) {
        replacements['{appointment_time}'] = formatTime(sampleRecipient.appointment_time);
      } else {
        replacements['{appointment_time}'] = '14:30';
      }
      
      // Replace all variables in the template
      Object.entries(replacements).forEach(([variable, value]) => {
        preview = preview.replace(new RegExp(variable, 'g'), value);
      });
      
      setPreviewMessage(preview);
    } else {
      setPreviewMessage('');
    }
  }, [messageTemplate, messageVariables, sampleRecipient]);


  // Handle message template change
  const handleMessageChange = (event) => {
    onMessageTemplateChange(event.target.value);
  };

  // Handle variable change
  const handleVariableChange = (variable, value) => {
    onMessageVariablesChange({
      ...messageVariables,
      [variable]: value
    });
  };

  // Insert variable at cursor position
  const insertVariable = (variable) => {
    const textarea = document.getElementById('message-template');
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = messageTemplate;
    const before = text.substring(0, start);
    const after = text.substring(end, text.length);
    
    onMessageTemplateChange(before + variable + after);
    
    // Set cursor position after the inserted variable
    setTimeout(() => {
      textarea.focus();
      textarea.selectionStart = start + variable.length;
      textarea.selectionEnd = start + variable.length;
    }, 0);
  };

  // Get time-based greeting
  const getTimeBasedGreeting = () => {
    const hour = new Date().getHours();
    
    if (hour >= 5 && hour < 12) {
      return "Bom dia";
    } else if (hour >= 12 && hour < 18) {
      return "Boa tarde";
    } else {
      return "Boa noite";
    }
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return '';
    
    const parts = date.split('-');
    if (parts.length != 3) return date;
    
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };

  // Format time
  const formatTime = (time) => {
    if (!time) return '';
    
    const parts = time.split(':');
    if (parts.length < 2) return time;
    
    return `${parts[0]}:${parts[1]}`;
  };

  return (
    <Box>
      {/* Removed redundant title */}
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              padding: '24px',
              backgroundColor: ColorPalette.white,
              borderRadius: '12px',
              border: `1px solid ${ColorPalette.border}`,
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.03)',
              marginBottom: '24px'
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: ColorPalette.textDark, marginBottom: '12px' }}>
              Compor Mensagem
            </Typography>
            
            <Box sx={{ marginBottom: '16px' }}>
              <TextField
                id="message-template"
                multiline
                rows={8}
                fullWidth
                variant="outlined"
                value={messageTemplate}
                onChange={handleMessageChange}
                placeholder="Digite sua mensagem aqui. Use as variáveis disponíveis para personalizar a mensagem para cada destinatário."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: ColorPalette.blueMain,
                      borderWidth: '2px'
                    }
                  }
                }}
              />
            </Box>
            
            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: ColorPalette.textDark, marginBottom: '8px' }}>
              Variáveis Disponíveis
            </Typography>
            
            <Box sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              backgroundColor: ColorPalette.blueLight4,
              padding: '12px',
              borderRadius: '8px',
              marginBottom: '16px'
            }}>
              {[
                '{name}',
                '{fullName}',
                '{greetings}',
                '{doctor_name}',
                '{company_name}',
                '{appointment_date}',
                '{appointment_time}'
              ].map((variable) => (
                <Button
                  key={variable}
                  variant="contained"
                  size="small"
                  onClick={() => insertVariable(variable)}
                  sx={{
                    textTransform: 'none',
                    backgroundColor: ColorPalette.white,
                    color: ColorPalette.blueMain,
                    fontWeight: 500,
                    boxShadow: 'none',
                    border: `1px solid ${ColorPalette.blueLight3}`,
                    '&:hover': {
                      backgroundColor: ColorPalette.blueLight4,
                      boxShadow: 'none',
                      borderColor: ColorPalette.blueMain
                    }
                  }}
                >
                  {variable}
                </Button>
              ))}
            </Box>
          </Paper>
          
          <Paper
            elevation={0}
            sx={{
              padding: '24px',
              backgroundColor: ColorPalette.white,
              borderRadius: '12px',
              border: `1px solid ${ColorPalette.border}`,
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.03)'
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: ColorPalette.textDark, marginBottom: '16px' }}>
              Configurar Variáveis
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nome do Profissional"
                  value={messageVariables.doctor_name || ''}
                  onChange={(e) => handleVariableChange('doctor_name', e.target.value)}
                  placeholder="Ex: Dr. Silva"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: ColorPalette.blueMain
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nome da Empresa"
                  value={messageVariables.company_name || ''}
                  onChange={(e) => handleVariableChange('company_name', e.target.value)}
                  placeholder="Ex: Clínica Exemplo"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: ColorPalette.blueMain
                      }
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              padding: '24px',
              backgroundColor: ColorPalette.white,
              borderRadius: '12px',
              border: `1px solid ${ColorPalette.border}`,
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.03)',
              marginBottom: '24px'
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: ColorPalette.textDark, marginBottom: '16px' }}>
              Pré-visualização da Mensagem
            </Typography>
            
            <Box sx={{
              padding: '16px',
              backgroundColor: ColorPalette.bgMain,
              borderRadius: '12px',
              minHeight: '200px',
              border: `1px solid ${ColorPalette.border}`,
              whiteSpace: 'pre-wrap',
              position: 'relative',
              backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' viewBox=\'0 0 100 100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z\' fill=\'%235a80fb\' fill-opacity=\'0.03\' fill-rule=\'evenodd\'/%3E%3C/svg%3E")',
              backgroundSize: '150px 150px'
            }}>
              <Box sx={{
                position: 'absolute',
                top: '0',
                left: '0',
                width: '100%',
                height: '40px',
                backgroundColor: ColorPalette.secondary,
                borderTopLeftRadius: '12px',
                borderTopRightRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                padding: '0 16px'
              }}>
                <Typography variant="subtitle2" sx={{ color: ColorPalette.white, fontWeight: 600 }}>
                  WhatsApp
                </Typography>
              </Box>
              
              <Box sx={{ marginTop: '48px', padding: '8px' }}>
                {previewMessage ? (
                  <Box sx={{
                    backgroundColor: ColorPalette.white,
                    padding: '12px',
                    borderRadius: '8px',
                    maxWidth: '80%',
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                    position: 'relative',
                    '&:after': {
                      content: '""',
                      position: 'absolute',
                      bottom: '8px',
                      right: '-6px',
                      width: '12px',
                      height: '12px',
                      backgroundColor: ColorPalette.white,
                      transform: 'rotate(45deg)',
                      boxShadow: '1px 1px 1px rgba(0, 0, 0, 0.05)'
                    }
                  }}>
                    <Typography variant="body2" sx={{ color: ColorPalette.textDark, whiteSpace: 'pre-wrap' }}>
                      {previewMessage}
                    </Typography>
                    <Typography variant="caption" sx={{ color: ColorPalette.textLight, display: 'block', textAlign: 'right', marginTop: '4px' }}>
                      {new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: ColorPalette.textLight, fontStyle: 'italic', textAlign: 'center', marginTop: '60px' }}>
                    A pré-visualização da mensagem aparecerá aqui...
                  </Typography>
                )}
              </Box>
            </Box>
          </Paper>
          
          <Paper
            elevation={0}
            sx={{
              padding: '24px',
              backgroundColor: ColorPalette.white,
              borderRadius: '12px',
              border: `1px solid ${ColorPalette.border}`,
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.03)'
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: ColorPalette.textDark, marginBottom: '16px' }}>
              Informações do Destinatário de Exemplo
            </Typography>
            
            <Box sx={{
              backgroundColor: ColorPalette.bgMain,
              borderRadius: '8px',
              padding: '16px',
              border: `1px solid ${ColorPalette.border}`
            }}>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Typography variant="body2" sx={{ color: ColorPalette.textMedium, fontWeight: 500 }}>
                    Nome:
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="body2" sx={{ color: ColorPalette.textDark }}>
                    {sampleRecipient ? sampleRecipient.name : 'João Silva'}
                  </Typography>
                </Grid>
                
                <Grid item xs={4}>
                  <Typography variant="body2" sx={{ color: ColorPalette.textMedium, fontWeight: 500 }}>
                    Telefone:
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="body2" sx={{ color: ColorPalette.textDark }}>
                    {sampleRecipient ? sampleRecipient.phone : '(11) 98765-4321'}
                  </Typography>
                </Grid>
                
                <Grid item xs={4}>
                  <Typography variant="body2" sx={{ color: ColorPalette.textMedium, fontWeight: 500 }}>
                    Email:
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="body2" sx={{ color: ColorPalette.textDark }}>
                    {sampleRecipient ? sampleRecipient.email : 'joao.silva@exemplo.com'}
                  </Typography>
                </Grid>
                
                {sampleRecipient && sampleRecipient.appointment_date && (
                  <>
                    <Grid item xs={4}>
                      <Typography variant="body2" sx={{ color: ColorPalette.textMedium, fontWeight: 500 }}>
                        Data da Consulta:
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <Typography variant="body2" sx={{ color: ColorPalette.textDark }}>
                        {formatDate(sampleRecipient.appointment_date)}
                      </Typography>
                    </Grid>
                  </>
                )}
                
                {sampleRecipient && sampleRecipient.appointment_time && (
                  <>
                    <Grid item xs={4}>
                      <Typography variant="body2" sx={{ color: ColorPalette.textMedium, fontWeight: 500 }}>
                        Horário da Consulta:
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <Typography variant="body2" sx={{ color: ColorPalette.textDark }}>
                        {formatTime(sampleRecipient.appointment_time)}
                      </Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MessageComposer;